import { QuietHours } from 'modules/domains/campaign';
import {
  AncestorIdData,
  AncestorNameData,
  FolderAncestorsData,
  FolderContentsData,
  FolderId,
  FolderIdData,
  FolderNameData,
} from 'modules/domains/foldering';
import { JourneySubfolder } from 'modules/domains/journey';
import { GraphQLTypeNameData, GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { GraphQLWorkflowNodeResourceFilter } from 'modules/workflows';
import { ElementOf } from 'utils/tsHelpers';

import {
  SELECTABLE_ITEM_TYPE,
  WORKFLOW_CAMPAIGN_DATA_SCIENCE_OPTIMIZATIONS,
  WORKFLOW_CAMPAIGN_MESSAGE_MEDIUMS,
  WORKFLOW_TRIGGER_EVENTS,
  WORKFLOW_EVENTS_TRIGGERED_BY_LIST,
  WORKFLOW_EVENTS_TRIGGERED_BY_CAMPAIGN,
  WORKFLOW_EVENTS_TRIGGERED_BY_CHANNEL,
  WORKFLOW_EVENTS_TRIGGERED_BY_CUSTOM_EVENT,
  WORKFLOW_EVENTS_TRIGGERED_BY_FIELD,
  WORKFLOW_STATUS_FILTER,
  WORKFLOW_STATUS_FILTER_WITH_DRAFTS,
} from './constants';

export type WorkflowStatusFilterOption = {
  id: WORKFLOW_STATUS_FILTER | WORKFLOW_STATUS_FILTER_WITH_DRAFTS;
  label: string;
};

export type JourneyLabel = string[];

export type WorkflowCreatorOrModifierUser = {
  id: string;
  fullName?: string;
  avatarUrl?: string;
};

export type WorkflowId = number;

export type WorkflowIdData = {
  id: WorkflowId;
};
export type WorkflowVisitorTracking = {
  visitorTrackingEnabled?: boolean;
};

export type WorkflowNameData = {
  name: string;
};

export type WorkflowDescriptionData = {
  description?: string;
};

export enum WorkflowType {
  DRAFT = 'Draft',
  PUBLISHED = 'Published',
}

export type WorkflowTypeData = {
  workflowType?: WorkflowType;
};

export type WorkflowStatusData = {
  isArchived: boolean;
  enabled: boolean;
  workflowType?: WorkflowType;
};

export type WorkflowTimestampData = {
  createdAt: string;
  updatedAt: string;
};

export type WorkflowUserData = {
  creatorUserId: string;
  updatedByUserId: string;
};

export type WorkflowAllTriggeredByWorkflowsData = {
  workflow: {
    name: string;
    id: WorkflowId;
  };
};

/**
 * Generated types from categorized workflow trigger event arrays
 */
export type WorkflowEventTriggeredByList = ElementOf<typeof WORKFLOW_EVENTS_TRIGGERED_BY_LIST>;
export type WorkflowEventTriggeredByCustomEvent = ElementOf<
  typeof WORKFLOW_EVENTS_TRIGGERED_BY_CUSTOM_EVENT
>;
export type WorkflowEventTriggeredByChannel = ElementOf<
  typeof WORKFLOW_EVENTS_TRIGGERED_BY_CHANNEL
>;
export type WorkflowEventTriggeredByCampaign = ElementOf<
  typeof WORKFLOW_EVENTS_TRIGGERED_BY_CAMPAIGN
>;
export type WorkflowEventTriggeredByField = ElementOf<typeof WORKFLOW_EVENTS_TRIGGERED_BY_FIELD>;

/**
 * Types for a workflow's start node `nodeData` property (i.e. `workflow.startNode.nodeData`),
 * including one for each trigger category
 */
export type TriggerNodeDataTypename<TriggerEvent extends WORKFLOW_TRIGGER_EVENTS> =
  `${TriggerEvent}Trigger`;
export type BaseTriggerNodeData<TriggerEvent extends WORKFLOW_TRIGGER_EVENTS> = {
  __typename: TriggerNodeDataTypename<TriggerEvent>;
};
export type ListTriggerNodeData = BaseTriggerNodeData<WorkflowEventTriggeredByList> & {
  emailListId: number;
};
export type CustomEventTriggerNodeData =
  BaseTriggerNodeData<WorkflowEventTriggeredByCustomEvent> & {
    eventName: string;
  };
export type ChannelTriggerNodeData = BaseTriggerNodeData<WorkflowEventTriggeredByChannel> & {
  channelId: number;
};
export type CampaignTriggerNodeData = BaseTriggerNodeData<WorkflowEventTriggeredByCampaign> & {
  emailCampaignId: number;
};
export type FieldTriggerNodeData = BaseTriggerNodeData<WorkflowEventTriggeredByField> & {
  field: string;
};

/**
 * Types for a worfklow's start node (i.e. 'workflow.startNode')
 */
export type BaseWorkflowStartNode<
  NodeData extends
    BaseTriggerNodeData<WORKFLOW_TRIGGER_EVENTS> = BaseTriggerNodeData<WORKFLOW_TRIGGER_EVENTS>,
> = {
  id: WorkflowNodeId;
  nodeData: NodeData;
};
export type WorkflowStartNodeWithResourceName<
  NodeData extends
    BaseTriggerNodeData<WORKFLOW_TRIGGER_EVENTS> = BaseTriggerNodeData<WORKFLOW_TRIGGER_EVENTS>,
> = BaseWorkflowStartNode<NodeData> & {
  resourceName: string;
};

/**
 * Partial workflow types with different variants of 'startNode'
 */
export type BaseWorkflowStartNodeData = {
  allTriggeredByWorkflows?: WorkflowAllTriggeredByWorkflowsData[];
  startNode?: BaseWorkflowStartNode;
};
export type TriggeredByListStartNodeData = BaseWorkflowStartNodeData & {
  startNode: WorkflowStartNodeWithResourceName<ListTriggerNodeData>;
};
export type TriggeredByCustomEventStartNodeData = BaseWorkflowStartNodeData & {
  startNode: BaseWorkflowStartNode<CustomEventTriggerNodeData>;
};
export type TriggeredByChannelStartNodeData = BaseWorkflowStartNodeData & {
  startNode: WorkflowStartNodeWithResourceName<ChannelTriggerNodeData>;
};
export type TriggeredByCampaignStartNodeData = BaseWorkflowStartNodeData & {
  startNode: WorkflowStartNodeWithResourceName<CampaignTriggerNodeData>;
};
export type TriggeredByFieldStartNodeData = BaseWorkflowStartNodeData & {
  startNode: BaseWorkflowStartNode<FieldTriggerNodeData>;
};
export type WorkflowStartNodeData =
  | BaseWorkflowStartNodeData
  | TriggeredByListStartNodeData
  | TriggeredByCustomEventStartNodeData
  | TriggeredByChannelStartNodeData
  | TriggeredByCampaignStartNodeData
  | TriggeredByFieldStartNodeData;

export type WorkflowNodeTemplateId = number;

export type WorkflowNodeCampaign = {
  id: number;
  templateId?: WorkflowNodeTemplateId;
  name: string;
  dataScienceOptimizations: WORKFLOW_CAMPAIGN_DATA_SCIENCE_OPTIMIZATIONS[];
  messageMedium: WORKFLOW_CAMPAIGN_MESSAGE_MEDIUMS;
  customQuietHours: QuietHours;
  nodeId?: WorkflowNodeId;
};

export type WorkflowNodeId = number;

export type WorkflowNode = {
  id: WorkflowNodeId;
  resource: {
    campaign?: WorkflowNodeCampaign;
  } | null;
};

export type WorkflowNodesData = {
  allNodes: WorkflowNode[];
};

export type WorkflowEdge = {
  srcWorkflowNodeId: number;
  destWorkflowNodeId: number;
};

export type WorkflowEdgesData = {
  edges: WorkflowEdge[];
};

export type WorkflowLabel = {
  id: number;
  name: string;
};

export type WorkflowLabelData = {
  labels?: WorkflowLabel[];
};

export type WorkflowReferenceData = {
  workflowReference?: {
    id: number;
    updatedAt: string;
    workflowType: WorkflowType;
    updatedByUserId: string;
  };
};

export type Workflow = WorkflowIdData &
  WorkflowNameData &
  WorkflowDescriptionData &
  WorkflowStatusData &
  WorkflowTimestampData &
  WorkflowUserData &
  WorkflowTypeData &
  WorkflowStartNodeData &
  WorkflowLabelData &
  WorkflowReferenceData;

export type WorkflowCampaignNodeMetadata = WorkflowStartNodeData &
  WorkflowNodesData &
  WorkflowEdgesData;

export type GraphQLWorkflow = Workflow & GraphQLTypeNameData<GRAPHQL_TYPENAMES.JOURNEY>;

export type GraphQLWorkflowFolderTreeViewQueryResponse = {
  workflowFolder: FolderIdData &
    FolderNameData &
    FolderAncestorsData<AncestorIdData & AncestorNameData> &
    FolderContentsData<JourneySubfolder, GraphQLWorkflow>;
};

export enum GraphQLWorkflowFolderContentFilter {
  contentItem = 'ContentItem',
  subfolder = 'Subfolder',
}

export type GraphQLWorkflowFolderTreeViewQueryVariables = {
  pagination: {
    limit: number;
    offset: number;
  };
  folderId: FolderId;
  content: GraphQLWorkflowFolderContentFilter[];
};

export type GraphQLWorkflowCampaignNodeMetadataQueryVariables = {
  workflowId: WorkflowId;
  workflowType?: WorkflowType;
  nodeResources: GraphQLWorkflowNodeResourceFilter[];
};

export type GraphQLWorkflowCampaignNodeMetadataQueryResponse = {
  workflow: WorkflowCampaignNodeMetadata;
};

export type GraphQLWorkflowCreatorsModifiersQueryResponse = {
  workflowCreators?: WorkflowCreatorOrModifierUser[];
  workflowModifiers?: WorkflowCreatorOrModifierUser[];
};

export type GraphQLRenameWorkflowFolderSuccess = {
  folderId: FolderId;
  folderName: string;
};

export type GraphQLRenameWorkflowFolderFailure = {
  id: FolderId;
  name: string;
};

export enum GraphQLMoveToWorkflowFolderKnownFailures {
  DestinationNotFoundFailure = 'DestinationNotFoundFailure',
  FolderIsAncestorOfDestinationFailure = 'FolderIsAncestorOfDestinationFailure',
  FolderNameConflictFailure = 'FolderNameConflictFailure',
  FolderNameNotFoundFailure = 'FolderNameNotFoundFailure',
  InternalError = 'InternalError',
  ParentNotFoundFailure = 'ParentNotFoundFailure',
  WorkflowDoesNotExistFailure = 'WorkflowDoesNotExistFailure',
}

export type GraphQLMoveToWorkflowFolderError = {
  id: FolderId | WorkflowId;
  name?: string;
  __typename: GraphQLMoveToWorkflowFolderKnownFailures;
};

export type GraphQLMoveToWorkflowFolderSuccess = {
  id: FolderId;
  name: string;
};

export type GraphQLMoveJourneyToFolderMutationVariables = {
  folderIds: FolderId[];
  content: { id: number; type?: WorkflowType }[];
  destinationId: FolderId;
};

export type GraphQLMoveToWorkflowFolderMutationResponse = {
  moveContentsAndFolders?: {
    movedContent: GraphQLMoveToWorkflowFolderSuccess[];
    errors: GraphQLMoveToWorkflowFolderError[];
  };
};

export type GraphQLRenameWorkflowFolderMutationResponse = {
  renameWorkflowFolder: {
    success: GraphQLRenameWorkflowFolderSuccess;
    failure: GraphQLRenameWorkflowFolderFailure;
  };
};

export type GraphQLRenameWorkflowFolderMutationVariables = {
  folderId: FolderId;
  newName: string;
};

export enum GraphQLArchiveWorkflowsAndDeleteFoldersKnownFailures {
  CannotDeleteRootFolderFailure = 'CannotDeleteRootFolderFailure',
  FolderDoesNotExistFailure = 'FolderDoesNotExistFailure',
}

export type GraphQLArchiveWorkflowsAndDeleteFoldersError = {
  id: FolderId | WorkflowId;
  name?: string;
  __typename: GraphQLArchiveWorkflowsAndDeleteFoldersKnownFailures;
};

export enum GraphQLArchiveWorkflowsAndDeleteFoldersSuccessTypenames {
  folder = 'FolderMutationSuccess',
  workflow = 'ContentItemSuccess',
}

export type GraphQLDeleteFolderSuccess = {
  id: FolderId;
  name: string;
  __typename: GraphQLArchiveWorkflowsAndDeleteFoldersSuccessTypenames.folder;
};

export type GraphQLArchiveWorkflowSuccess = {
  id: WorkflowId;
  name: string;
  __typename: GraphQLArchiveWorkflowsAndDeleteFoldersSuccessTypenames.workflow;
};

export type GraphQLArchiveWorkflowsAndDeleteFoldersMutationResponseData = {
  deleteContentsAndFolders: {
    folderFailures: GraphQLArchiveWorkflowsAndDeleteFoldersError[];
    deletedContent: (GraphQLDeleteFolderSuccess | GraphQLArchiveWorkflowSuccess)[];
  };
};

export type GraphQLDeleteJourneyToFolderMutationVariables = {
  folderIds: FolderId[];
  content: { id: number; type?: WorkflowType }[];
};

export type ExitRuleExitsByNode = {
  [nodeId: string]: { [ruleId: string]: number };
};
/**
 * Properties on the REST API workflows object pertaining to visitor count
 */
export type LegacyWorkflowVisitorCountData = {
  inProgress: number;
  steps: number;
  finished: number;
  started: number;
  exitRuleExitsByNode: ExitRuleExitsByNode;
};

/**
 * Workflow returned by the REST API
 */
export type LegacyWorkflow = WorkflowIdData &
  WorkflowNameData &
  WorkflowStatusData &
  WorkflowTimestampData &
  WorkflowUserData &
  LegacyWorkflowVisitorCountData;

export type WorkflowIndexReduxState = {
  thumbnails: Record<WorkflowNodeTemplateId, string | null>;
  sendActivity: Record<WorkflowId, LegacyWorkflowVisitorCountData | null>;
  selectedItems: Record<SELECTABLE_ITEM_TYPE | string, Set<number>>;
};

export type ReduxStateWithWorkflowIndex = {
  workflowIndex: WorkflowIndexReduxState;
};

export type SelectableItem = {
  id: number;
  type: SELECTABLE_ITEM_TYPE;
};
